import React, { useState, useEffect, useRef } from 'react';
import { BarChart2, AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import { sitesApi } from '../../api/client';
import type { Site } from '../../schemas/validation';
import { ImageUrlWithUpload } from '../../components/ImageUrlWithUpload';
import FeatureGate from '../../components/FeatureGate';

interface SeoSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site;
  onSiteUpdate: (site: Site) => void;
  onShowToast: (message: string, type: 'success' | 'error') => void;
  /** Called after a successful save so the caller can refresh derived state (e.g. unpublished summary) */
  onAfterUpdate?: () => void | Promise<void>;
  /** Called after a successful save to prompt the user to Publish All */
  onRequestPublishPrompt?: () => void;
}

export const SeoSettingsModal: React.FC<SeoSettingsModalProps> = ({
  isOpen,
  onClose,
  site,
  onSiteUpdate,
  onShowToast,
  onAfterUpdate,
  onRequestPublishPrompt,
}) => {
  const [isSavingSeo, setIsSavingSeo] = useState(false);
  const [ogImageUrl, setOgImageUrl] = useState(site.ogImage || '');
  const [nativeAnalyticsEnabled, setNativeAnalyticsEnabled] = useState(
    site.nativeAnalyticsEnabled !== false
  );
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showEnabledMessage, setShowEnabledMessage] = useState(false);

  // Privacy banner state
  const [privacyBannerEnabled, setPrivacyBannerEnabled] = useState(
    (site as any).privacyBannerEnabled || false
  );
  const [privacyBannerMessage, setPrivacyBannerMessage] = useState(
    (site as any).privacyBannerMessage || ''
  );
  const [showPrivacyDisableWarning, setShowPrivacyDisableWarning] = useState(false);
  const [privacyBannerAutoEnabled, setPrivacyBannerAutoEnabled] = useState(false);

  // Meta CAPI state
  const [metaAccessToken, setMetaAccessToken] = useState('');
  const [metaTestEventCode, setMetaTestEventCode] = useState((site as any).metaTestEventCode || '');
  const hasMetaAccessToken = (site as any).hasMetaAccessToken || false;

  // Track initial tracking IDs to detect when they're first added
  const initialTrackingRef = useRef({
    googleAnalyticsId: site.googleAnalyticsId || '',
    googleTagManagerId: site.googleTagManagerId || '',
    metaPixelId: site.metaPixelId || '',
  });

  // Reset state when modal opens with new site data
  useEffect(() => {
    if (isOpen) {
      setOgImageUrl(site.ogImage || '');
      setNativeAnalyticsEnabled(site.nativeAnalyticsEnabled !== false);
      setShowDisableConfirm(false);
      setShowEnabledMessage(false);
      setPrivacyBannerEnabled((site as any).privacyBannerEnabled || false);
      setPrivacyBannerMessage((site as any).privacyBannerMessage || '');
      setShowPrivacyDisableWarning(false);
      setPrivacyBannerAutoEnabled(false);
      // Reset CAPI fields (token is never pre-filled for security)
      setMetaAccessToken('');
      setMetaTestEventCode((site as any).metaTestEventCode || '');
      initialTrackingRef.current = {
        googleAnalyticsId: site.googleAnalyticsId || '',
        googleTagManagerId: site.googleTagManagerId || '',
        metaPixelId: site.metaPixelId || '',
      };
    }
  }, [
    isOpen,
    site.ogImage,
    site.nativeAnalyticsEnabled,
    site.googleAnalyticsId,
    site.googleTagManagerId,
    site.metaPixelId,
    (site as any).privacyBannerEnabled,
    (site as any).privacyBannerMessage,
    (site as any).metaTestEventCode,
  ]);

  const handleToggleAnalytics = () => {
    if (nativeAnalyticsEnabled) {
      // Trying to turn OFF - show confirmation
      setShowDisableConfirm(true);
      setShowEnabledMessage(false);
    } else {
      // Turning ON - show success message
      setNativeAnalyticsEnabled(true);
      setShowDisableConfirm(false);
      setShowEnabledMessage(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowEnabledMessage(false), 3000);
    }
  };

  const confirmDisableAnalytics = () => {
    setNativeAnalyticsEnabled(false);
    setShowDisableConfirm(false);
  };

  const cancelDisableAnalytics = () => {
    setShowDisableConfirm(false);
  };

  // Privacy banner toggle handler
  const handleTogglePrivacyBanner = () => {
    if (privacyBannerEnabled) {
      // Check if tracking is configured before allowing disable
      const form = document.querySelector('form');
      const formData = form ? new FormData(form) : null;
      const hasTracking =
        formData &&
        ((formData.get('googleAnalyticsId') as string)?.trim() ||
          (formData.get('googleTagManagerId') as string)?.trim() ||
          (formData.get('metaPixelId') as string)?.trim());

      if (hasTracking) {
        setShowPrivacyDisableWarning(true);
      } else {
        setPrivacyBannerEnabled(false);
        setPrivacyBannerAutoEnabled(false);
      }
    } else {
      setPrivacyBannerEnabled(true);
    }
  };

  const confirmDisablePrivacyBanner = () => {
    setPrivacyBannerEnabled(false);
    setShowPrivacyDisableWarning(false);
    setPrivacyBannerAutoEnabled(false);
  };

  const cancelDisablePrivacyBanner = () => {
    setShowPrivacyDisableWarning(false);
  };

  // Auto-enable privacy banner when tracking ID is added
  const handleTrackingIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const wasEmpty = !initialTrackingRef.current[name as keyof typeof initialTrackingRef.current];
    const isNowFilled = value.trim().length > 0;

    if (wasEmpty && isNowFilled && !privacyBannerEnabled) {
      setPrivacyBannerEnabled(true);
      setPrivacyBannerAutoEnabled(true);
      onShowToast('Privacy banner has been enabled for GDPR compliance.', 'success');
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      setIsSavingSeo(true);

      const seoData: Record<string, any> = {
        seoTitle: (formData.get('seoTitle') as string) || null,
        seoDescription: (formData.get('seoDescription') as string) || null,
        seoKeywords: (formData.get('seoKeywords') as string) || null,
        ogTitle: (formData.get('ogTitle') as string) || null,
        ogDescription: (formData.get('ogDescription') as string) || null,
        ogImage: ogImageUrl || null,
        nativeAnalyticsEnabled,
        googleAnalyticsId: (formData.get('googleAnalyticsId') as string) || null,
        googleTagManagerId: (formData.get('googleTagManagerId') as string) || null,
        metaPixelId: (formData.get('metaPixelId') as string) || null,
        customHeadScripts: (formData.get('customHeadScripts') as string) || null,
        customBodyScripts: (formData.get('customBodyScripts') as string) || null,
        privacyBannerEnabled,
        privacyBannerMessage: privacyBannerMessage || null,
        metaTestEventCode: metaTestEventCode || null,
      };

      // Only include metaAccessToken if user entered a new one (save-to-overwrite pattern)
      if (metaAccessToken.trim()) {
        seoData.metaAccessToken = metaAccessToken;
      }

      const updatedSite = await sitesApi.update(site.id, seoData);
      onSiteUpdate(updatedSite);
      onShowToast(
        "SEO & Analytics settings saved. They'll appear on your live site after you publish.",
        'success'
      );
      await onAfterUpdate?.();
      onClose();
      onRequestPublishPrompt?.();
    } catch (error) {
      console.error('Failed to save SEO settings:', error);
      onShowToast('Failed to save SEO settings. Please try again.', 'error');
    } finally {
      setIsSavingSeo(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={() => !isSavingSeo && onClose()}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    SEO & Analytics Settings
                  </h3>

                  {/* Basic SEO Section */}
                  <div className="space-y-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-900">Basic SEO</h4>
                    <div>
                      <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700">
                        Default Page Title
                      </label>
                      <input
                        type="text"
                        name="seoTitle"
                        id="seoTitle"
                        defaultValue={site.seoTitle || ''}
                        placeholder="My Website"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Used when pages don&apos;t have a custom title
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="seoDescription"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Default Meta Description
                      </label>
                      <textarea
                        name="seoDescription"
                        id="seoDescription"
                        rows={2}
                        defaultValue={site.seoDescription || ''}
                        placeholder="A brief description of your website"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Analytics Section */}
                  <div className="border-t border-gray-200 pt-6 space-y-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-900">Analytics & Tracking</h4>

                    {/* Native Analytics Toggle */}
                    <div
                      className={`rounded-lg p-4 ${nativeAnalyticsEnabled ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-lg ${nativeAnalyticsEnabled ? 'bg-purple-100' : 'bg-gray-200'}`}
                          >
                            <BarChart2
                              className={`h-5 w-5 ${nativeAnalyticsEnabled ? 'text-purple-600' : 'text-gray-400'}`}
                            />
                          </div>
                          <div>
                            <h5
                              className={`text-sm font-medium ${nativeAnalyticsEnabled ? 'text-purple-900' : 'text-gray-700'}`}
                            >
                              Native Analytics
                            </h5>
                            <p
                              className={`text-xs ${nativeAnalyticsEnabled ? 'text-purple-700' : 'text-gray-500'}`}
                            >
                              Built-in privacy-friendly analytics for page views, navigation paths,
                              and visitor insights.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={nativeAnalyticsEnabled}
                          onClick={handleToggleAnalytics}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            nativeAnalyticsEnabled ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              nativeAnalyticsEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Enabled success message */}
                      {showEnabledMessage && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-2">
                          <CheckCircle className="h-4 w-4 flex-shrink-0" />
                          <span>
                            Native analytics enabled! Your site will now collect visitor insights.
                          </span>
                        </div>
                      )}

                      {/* Disable confirmation */}
                      {showDisableConfirm && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-amber-800">
                                Disable native analytics?
                              </p>
                              <p className="text-xs text-amber-700 mt-1">
                                Your site will stop collecting page views and visitor data. Existing
                                analytics data will be preserved.
                              </p>
                              <div className="mt-3 flex gap-2">
                                <button
                                  type="button"
                                  onClick={confirmDisableAnalytics}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                >
                                  Yes, disable
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelDisableAnalytics}
                                  className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Third-Party Analytics */}
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-3">Third-party tracking (optional)</p>
                    </div>
                    <div>
                      <label
                        htmlFor="googleAnalyticsId"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Google Analytics ID
                      </label>
                      <input
                        type="text"
                        name="googleAnalyticsId"
                        id="googleAnalyticsId"
                        defaultValue={site.googleAnalyticsId || ''}
                        placeholder="G-XXXXXXXXXX"
                        onChange={handleTrackingIdChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="googleTagManagerId"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Google Tag Manager ID
                      </label>
                      <input
                        type="text"
                        name="googleTagManagerId"
                        id="googleTagManagerId"
                        defaultValue={site.googleTagManagerId || ''}
                        placeholder="GTM-XXXXXXX"
                        onChange={handleTrackingIdChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="metaPixelId"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Meta Pixel ID
                      </label>
                      <input
                        type="text"
                        name="metaPixelId"
                        id="metaPixelId"
                        defaultValue={site.metaPixelId || ''}
                        placeholder="XXXXXXXXXXXXXXXX"
                        onChange={handleTrackingIdChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Client-side tracking via the Meta Pixel (formerly Facebook Pixel)
                      </p>
                    </div>

                    {/* Meta Conversions API Section - Paid Feature */}
                    <FeatureGate feature="meta_capi" showUpgradePrompt={true}>
                      <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <h5 className="text-sm font-medium text-gray-900">
                            Meta Conversions API (CAPI)
                          </h5>
                          <div className="group relative">
                            <svg
                              className="h-4 w-4 text-gray-400 cursor-help"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <div className="hidden group-hover:block absolute left-0 top-5 z-10 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
                              Server-side tracking for better accuracy, especially with iOS 14+
                              privacy restrictions and ad blockers.
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label
                              htmlFor="metaAccessToken"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Access Token
                              {hasMetaAccessToken && (
                                <span className="ml-2 text-xs text-green-600">(Token saved)</span>
                              )}
                            </label>
                            <input
                              type="password"
                              id="metaAccessToken"
                              value={metaAccessToken}
                              onChange={e => setMetaAccessToken(e.target.value)}
                              placeholder={
                                hasMetaAccessToken
                                  ? 'Enter new token to replace'
                                  : 'Enter your access token'
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Generate in Meta Events Manager → Settings → Conversions API.{' '}
                              <a
                                href="https://www.facebook.com/business/help/2041148702652965"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:underline"
                              >
                                Learn more
                              </a>
                            </p>
                          </div>

                          {/* Test Event Code - only show when Pixel ID is set */}
                          {site.metaPixelId && (
                            <div>
                              <label
                                htmlFor="metaTestEventCode"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Test Event Code
                                <span className="ml-2 text-xs text-gray-500">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                id="metaTestEventCode"
                                value={metaTestEventCode}
                                onChange={e => setMetaTestEventCode(e.target.value)}
                                placeholder="TEST12345"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Events will appear in Meta Events Manager → Test Events. Remove when
                                done testing.
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                          <strong>How it works:</strong> Page views and form submissions are
                          automatically sent server-side. Set up Custom Conversions in Meta Events
                          Manager to track specific pages (e.g., thank-you page) as conversions.
                        </div>
                      </div>
                    </FeatureGate>

                    {/* Privacy Banner Section */}
                    <div
                      className={`rounded-lg p-4 mt-4 ${privacyBannerEnabled ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-lg ${privacyBannerEnabled ? 'bg-blue-100' : 'bg-gray-200'}`}
                          >
                            <Shield
                              className={`h-5 w-5 ${privacyBannerEnabled ? 'text-blue-600' : 'text-gray-400'}`}
                            />
                          </div>
                          <div>
                            <h5
                              className={`text-sm font-medium ${privacyBannerEnabled ? 'text-blue-900' : 'text-gray-700'}`}
                            >
                              Privacy Banner
                            </h5>
                            <p
                              className={`text-xs ${privacyBannerEnabled ? 'text-blue-700' : 'text-gray-500'}`}
                            >
                              Show a consent banner for GDPR compliance. Third-party tracking will
                              only load after visitors accept.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={privacyBannerEnabled}
                          onClick={handleTogglePrivacyBanner}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            privacyBannerEnabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              privacyBannerEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Auto-enabled notification */}
                      {privacyBannerAutoEnabled && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-2">
                          <CheckCircle className="h-4 w-4 flex-shrink-0" />
                          <span>
                            Privacy banner was automatically enabled when you added tracking.
                          </span>
                        </div>
                      )}

                      {/* Disable warning */}
                      {showPrivacyDisableWarning && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-amber-800">
                                Disable privacy banner?
                              </p>
                              <p className="text-xs text-amber-700 mt-1">
                                Disabling the privacy banner while tracking is configured may
                                violate GDPR and other privacy regulations. Third-party tracking
                                scripts will load immediately without visitor consent.
                              </p>
                              <div className="mt-3 flex gap-2">
                                <button
                                  type="button"
                                  onClick={confirmDisablePrivacyBanner}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                >
                                  Yes, disable
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelDisablePrivacyBanner}
                                  className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Custom message field */}
                      {privacyBannerEnabled && (
                        <div className="mt-3">
                          <label
                            htmlFor="privacyBannerMessage"
                            className="block text-xs font-medium text-gray-700 mb-1"
                          >
                            Custom Banner Message (optional)
                          </label>
                          <textarea
                            id="privacyBannerMessage"
                            value={privacyBannerMessage}
                            onChange={e => setPrivacyBannerMessage(e.target.value)}
                            placeholder="We use cookies and tracking technologies to improve your experience..."
                            rows={2}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave empty to use the default message.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Social Media Section */}
                  <div className="border-t border-gray-200 pt-6 space-y-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-900">
                      Social Media Sharing (Open Graph)
                    </h4>
                    <div>
                      <label htmlFor="ogTitle" className="block text-sm font-medium text-gray-700">
                        Social Title
                      </label>
                      <input
                        type="text"
                        name="ogTitle"
                        id="ogTitle"
                        defaultValue={site.ogTitle || ''}
                        placeholder="My Website"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="ogDescription"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Social Description
                      </label>
                      <textarea
                        name="ogDescription"
                        id="ogDescription"
                        rows={2}
                        defaultValue={site.ogDescription || ''}
                        placeholder="A brief description shown when sharing"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <ImageUrlWithUpload
                      value={ogImageUrl}
                      onChange={setOgImageUrl}
                      siteId={site.id}
                      label="Social Image"
                      placeholder="https://example.com/image.jpg"
                      recommendedSize="1200x630px"
                      helpText="Image shown when sharing on social media"
                    />
                  </div>

                  {/* Custom Scripts Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Custom Scripts</h4>
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="customHeadScripts"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Custom Head Scripts
                        </label>
                        <textarea
                          name="customHeadScripts"
                          id="customHeadScripts"
                          rows={3}
                          defaultValue={site.customHeadScripts || ''}
                          placeholder="<!-- Add custom scripts here -->"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono text-xs"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Scripts added to the &lt;head&gt; section
                        </p>
                      </div>
                      <div>
                        <label
                          htmlFor="customBodyScripts"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Custom Body Scripts
                        </label>
                        <textarea
                          name="customBodyScripts"
                          id="customBodyScripts"
                          rows={3}
                          defaultValue={site.customBodyScripts || ''}
                          placeholder="<!-- Add custom scripts here -->"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono text-xs"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Scripts added before the closing &lt;/body&gt; tag
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSavingSeo}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingSeo ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
              <button
                type="button"
                onClick={() => !isSavingSeo && onClose()}
                disabled={isSavingSeo}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
